import { useState, useEffect } from "react";
import axios from "axios";
import { UrlApi } from "../../services/apirest";
import Navbar from "../Navbar";

export default function ComparaTablas() {
  const [base1, setBase1] = useState("apirestdev");
  const [base2, setBase2] = useState("apirestprod");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [databases, setDatabases] = useState([]);
  const [expandedTables, setExpandedTables] = useState(new Set());

  useEffect(() => {
    const loadDatabases = async () => {
      try {
        // Usando la nueva ruta
        const res = await axios.get(`${UrlApi}/comparatablas/databases`);
        setDatabases(res.data);
      } catch (err) {
        console.error("Error cargando bases:", err);
        setError("Error al cargar las bases de datos");
      }
    };
    loadDatabases();
  }, []);

  const comparar = async () => {
    if (!base1 || !base2) {
      setError("Por favor selecciona ambas bases de datos");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      // Usando la nueva ruta
      const res = await axios.post(`${UrlApi}/comparatablas/compare`, {
        base1,
        base2,
      });
      setData(res.data);
      
      // Auto-expandir tablas que tienen diferencias
      const agrupado = agruparPorTabla(res.data);
      const nuevasExpandidas = new Set();
      Object.entries(agrupado).forEach(([tableName, columns]) => {
        const tieneDiferencias = columns.some(col => col.estado !== 'igual');
        if (tieneDiferencias) {
          nuevasExpandidas.add(tableName);
        }
      });
      setExpandedTables(nuevasExpandidas);
      
    } catch (err) {
      setError("Error al comparar: " + (err.response?.data?.details || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleTable = (tableName) => {
    const newExpanded = new Set(expandedTables);
    if (newExpanded.has(tableName)) {
      newExpanded.delete(tableName);
    } else {
      newExpanded.add(tableName);
    }
    setExpandedTables(newExpanded);
  };

  const agruparPorTabla = (data) => {
    return data.reduce((acc, row) => {
      if (!acc[row.TABLE_NAME]) {
        acc[row.TABLE_NAME] = [];
      }
      acc[row.TABLE_NAME].push(row);
      return acc;
    }, {});
  };

  const dataAgrupada = agruparPorTabla(data);

  const getColorByEstado = (estado) => {
    switch(estado) {
      case 'solo_en_db1': return '#f59e0b';
      case 'solo_en_db2': return '#8b5cf6';
      case 'diferente': return '#ef4444';
      default: return '#10b981';
    }
  };

  const getEstadoIcon = (estado) => {
    switch(estado) {
      case 'solo_en_db1': return '⚠️';
      case 'solo_en_db2': return '⚠️';
      case 'diferente': return '🔄';
      default: return '✓';
    }
  };

  const getEstadoTexto = (estado) => {
    switch(estado) {
      case 'solo_en_db1': return `Solo en ${base1}`;
      case 'solo_en_db2': return `Solo en ${base2}`;
      case 'diferente': return 'Diferente';
      default: return 'Igual';
    }
  };

  const getDetallesDiferencia = (col) => {
    if (col.estado === 'solo_en_db1') {
      return `📁 ${col.tipo_db1} • ${col.null_db1 === 'YES' ? 'NULL' : 'NOT NULL'}`;
    }
    if (col.estado === 'solo_en_db2') {
      return `📁 ${col.tipo_db2} • ${col.null_db2 === 'YES' ? 'NULL' : 'NOT NULL'}`;
    }
    if (col.estado === 'diferente') {
      return `📁 ${base1}: ${col.tipo_db1} (${col.null_db1 === 'YES' ? 'NULL' : 'NOT NULL'})  |  ${base2}: ${col.tipo_db2} (${col.null_db2 === 'YES' ? 'NULL' : 'NOT NULL'})`;
    }
    return `📁 ${col.tipo_db1} • ${col.null_db1 === 'YES' ? 'NULL' : 'NOT NULL'}`;
  };

  const totalColumnas = data.length;
  const iguales = data.filter(col => col.estado === 'igual').length;
  const diferencias = data.filter(col => col.estado !== 'igual').length;

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
    },
    wrapper: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    card: {
      background: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
      overflow: 'hidden',
      marginBottom: '24px',
    },
    header: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '30px',
      color: 'white',
      textAlign: 'center',
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      margin: '0 0 8px 0',
    },
    subtitle: {
      fontSize: '14px',
      opacity: 0.9,
      margin: 0,
    },
    panel: {
      padding: '30px',
    },
    selectGroup: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '20px',
      marginBottom: '24px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '600',
      color: '#4a5568',
      marginBottom: '8px',
    },
    select: {
      width: '100%',
      padding: '10px 12px',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'all 0.3s',
      outline: 'none',
    },
    button: {
      width: '100%',
      padding: '12px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
    },
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
    },
    statCard: {
      background: '#f7fafc',
      padding: '16px',
      borderRadius: '12px',
      textAlign: 'center',
      borderLeft: '4px solid',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      margin: '8px 0',
    },
    statLabel: {
      fontSize: '13px',
      color: '#4a5568',
      textTransform: 'uppercase',
      fontWeight: '600',
    },
    tableCard: {
      background: 'white',
      borderRadius: '12px',
      marginBottom: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      overflow: 'hidden',
    },
    tableHeader: {
      background: '#f7fafc',
      padding: '16px 20px',
      borderBottom: '2px solid #e2e8f0',
      fontWeight: 'bold',
      fontSize: '18px',
      color: '#2d3748',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      transition: 'background 0.2s',
    },
    tableHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    expandIcon: {
      fontSize: '20px',
      transition: 'transform 0.2s',
      display: 'inline-block',
    },
    badgeDiferencias: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      background: '#fee2e2',
      color: '#dc2626',
    },
    badgeSinDiferencias: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      background: '#d1fae5',
      color: '#059669',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      textAlign: 'left',
      padding: '12px 20px',
      background: '#f7fafc',
      borderBottom: '2px solid #e2e8f0',
      fontSize: '13px',
      fontWeight: '600',
      color: '#4a5568',
    },
    td: {
      padding: '12px 20px',
      borderBottom: '1px solid #e2e8f0',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: 'white',
      fontSize: '18px',
    },
    error: {
      background: '#fed7d7',
      color: '#c53030',
      padding: '12px 20px',
      borderRadius: '8px',
      marginBottom: '20px',
      borderLeft: '4px solid #c53030',
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      color: '#718096',
    },
  };

  return (
    <div style={styles.container}>
        <Navbar/>
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.card}>
          <div style={styles.header}>
            <h1 style={styles.title}>📊 Comparador de Bases de Datos</h1>
            <p style={styles.subtitle}>Compara estructuras de tablas entre dos bases MySQL</p>
          </div>

          {/* Controls */}
          <div style={styles.panel}>
            <div style={styles.selectGroup}>
              <div>
                <label style={styles.label}>Base de Datos 1</label>
                <select
                  value={base1}
                  onChange={(e) => setBase1(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="">Selecciona una base</option>
                  {databases.map(db => (
                    <option key={db} value={db}>{db}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={styles.label}>Base de Datos 2</label>
                <select
                  value={base2}
                  onChange={(e) => setBase2(e.target.value)}
                  style={styles.select}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="">Selecciona una base</option>
                  {databases.map(db => (
                    <option key={db} value={db}>{db}</option>
                  ))}
                </select>
              </div>
            </div>

            <button
              onClick={comparar}
              disabled={loading}
              style={styles.button}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 20px rgba(0,0,0,0.2)';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              {loading ? '⏳ Comparando...' : ' Comparar Bases de Datos'}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.error}>
            ❌ {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={styles.loading}>
            <div>🔄 Analizando estructuras...</div>
          </div>
        )}

        {/* Results */}
        {!loading && data.length > 0 && (
          <>
            {/* Stats */}
            <div style={styles.stats}>
              <div style={{ ...styles.statCard, borderLeftColor: '#667eea' }}>
                <div style={styles.statLabel}>Total Columnas</div>
                <div style={styles.statValue}>{totalColumnas}</div>
              </div>
              <div style={{ ...styles.statCard, borderLeftColor: '#10b981' }}>
                <div style={styles.statLabel}>Columnas Iguales</div>
                <div style={styles.statValue}>{iguales}</div>
              </div>
              <div style={{ ...styles.statCard, borderLeftColor: '#ef4444' }}>
                <div style={styles.statLabel}>Diferencias</div>
                <div style={styles.statValue}>{diferencias}</div>
              </div>
              <div style={{ ...styles.statCard, borderLeftColor: '#8b5cf6' }}>
                <div style={styles.statLabel}>Tablas</div>
                <div style={styles.statValue}>{Object.keys(dataAgrupada).length}</div>
              </div>
            </div>

            {/* Tables */}
            {Object.entries(dataAgrupada).map(([tabla, columnas]) => {
              const tieneDiferencias = columnas.some(col => col.estado !== 'igual');
              const isExpanded = expandedTables.has(tabla);
              
              return (
                <div key={tabla} style={styles.tableCard}>
                  <div 
                    style={styles.tableHeader}
                    onClick={() => toggleTable(tabla)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f7fafc';
                    }}
                  >
                    <div style={styles.tableHeaderLeft}>
                      <span style={{
                        ...styles.expandIcon,
                        transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                      }}>
                        ▶
                      </span>
                      <span> {tabla}</span>
                      <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>
                        ({columnas.length} columnas)
                      </span>
                    </div>
                    {tieneDiferencias ? (
                      <span style={styles.badgeDiferencias}>
                        ⚠️ {columnas.filter(c => c.estado !== 'igual').length} diferencias
                      </span>
                    ) : (
                      <span style={styles.badgeSinDiferencias}>
                        ✓ Sin diferencias
                      </span>
                    )}
                  </div>
                  
                  {isExpanded && (
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>Columna</th>
                          <th style={styles.th}>Estado</th>
                          <th style={styles.th}>Detalles</th>
                        </tr>
                      </thead>
                      <tbody>
                        {columnas.map((col, i) => {
                          const estadoTexto = getEstadoTexto(col.estado);
                          const color = getColorByEstado(col.estado);
                          
                          return (
                            <tr key={i}>
                              <td style={styles.td}>
                                {getEstadoIcon(col.estado)} {col.COLUMN_NAME}
                              </td>
                              <td style={styles.td}>
                                <span style={{
                                  ...styles.badge,
                                  background: `${color}15`,
                                  color: color,
                                  border: `1px solid ${color}30`
                                }}>
                                  {estadoTexto}
                                </span>
                              </td>
                              <td style={{ ...styles.td, fontSize: '13px', color: '#4a5568' }}>
                                {getDetallesDiferencia(col)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </>
        )}

        {/* Empty State */}
        {!loading && data.length === 0 && !error && (
          <div style={styles.card}>
            <div style={styles.empty}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
              <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No hay datos para mostrar</div>
              <div style={{ fontSize: '14px', color: '#718096' }}>Selecciona dos bases de datos y haz clic en "Comparar"</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}